import { apiRequest } from "utils/api";
import { AppDispatch } from "store";
import { TableSortBy } from "components/reportTable";
import { SET_RECRUITER_IDS_SUCCESS } from "store/reducers/types/recruiterId";
import { READ_TASKS_DATA_FAILURE, READ_TASKS_DATA_SUCCESS, READ_TASKS_DATA_REQUEST } from "store/reducers/types/dashboard/tasks";
import { READ_TALENT_TABLE_DATA_REQUEST, READ_TALENT_TABLE_DATA_SUCCESS, READ_TALENT_TABLE_DATA_FAILURE } from "store/reducers/types/dashboard/talent";
import { READ_JOBS_DATA_REQUEST, READ_JOBS_DATA_SUCCESS, READ_JOBS_DATA_FAILURE } from "store/reducers/types/dashboard/jobs";
import { READ_ISSUES_DATA_REQUEST, READ_ISSUES_DATA_SUCCESS, READ_ISSUES_DATA_FAILURE } from "store/reducers/types/dashboard/issues";
import { READ_PERFORMANCE_DATA_REQUEST, READ_PERFORMANCE_DATA_SUCCESS, READ_PERFORMANCE_DATA_FAILURE } from "store/reducers/types/dashboard/performance";
import { READ_SLOTS_DATA_REQUEST, READ_SLOTS_DATA_SUCCESS, READ_SLOTS_DATA_FAILURE } from "store/reducers/types/dashboard/slots";
import { READ_RECRUITER_DROPDOWN_DATA_REQUEST, READ_RECRUITER_DROPDOWN_DATA_SUCCESS, READ_RECRUITER_DROPDOWN_DATA_FAILURE } from "store/reducers/types/dashboard/recruiters";
import { READ_RECRUITMENT_REPORT_BY_METRO_DATA_REQUEST, READ_RECRUITMENT_REPORT_BY_METRO_DATA_SUCCESS, READ_RECRUITMENT_REPORT_BY_METRO_DATA_FAILURE } from "store/reducers/types/dashboard/recruitmentReportByMetro";
import { READ_FUTURE_WORK_BY_CLIENT_DATA_REQUEST, READ_FUTURE_WORK_BY_CLIENT_DATA_SUCCESS, READ_FUTURE_WORK_BY_CLIENT_DATA_FAILURE } from "store/reducers/types/dashboard/futureWorkByClient";
import { READ_FUTURE_WORK_BY_DATA_REQUEST, READ_FUTURE_WORK_BY_DATA_SUCCESS, READ_FUTURE_WORK_BY_DATA_FAILURE } from "store/reducers/types/dashboard/futureWorkBy";
import { READ_METRO_RECRUITERS_DATA_REQUEST, READ_METRO_RECRUITERS_DATA_SUCCESS, READ_METRO_RECRUITERS_DATA_FAILURE } from "store/reducers/types/dashboard/metroRecruiters";
import { READ_METRO_CLIENTS_DATA_REQUEST, READ_METRO_CLIENTS_DATA_SUCCESS, READ_METRO_CLIENTS_DATA_FAILURE } from "store/reducers/types/dashboard/metroClients";
import { READ_CLIENT_STORES_DATA_REQUEST, READ_CLIENT_STORES_DATA_SUCCESS, READ_CLIENT_STORES_DATA_FAILURE } from "store/reducers/types/dashboard/clientStores";
import { READ_DEDICATED_WORKER_REPORT_DATA_FAILURE, READ_DEDICATED_WORKER_REPORT_DATA_REQUEST, READ_DEDICATED_WORKER_REPORT_DATA_SUCCESS } from 'store/reducers/types/dashboard/dedicatedWorkerReport';
import { READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_FAILURE, READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_REQUEST, READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_SUCCESS } from "store/reducers/types/dashboard/travelMerchandiserDedicatedReport";

type BaseParams = {
  userIds: number[];
};

export enum PerformanceDataRange {
  Day = 'Day',
  Week = 'Week',
  Month = 'Month'
};

export enum SlotsDataRange {
  Today = 'Today',
  NextWeek = 'NextWeek',
  NextTwoWeeks = 'NextTwoWeeks',
  NextMonth = 'NextMonth',
  LastMonth = 'LastMonth',
  YTD = 'YTD'
};

type PerformanceDataParams = BaseParams & {
  dataRange: PerformanceDataRange;
};

type SlotsDataParams = BaseParams & {
  dataRange: SlotsDataRange;
}

type RecruitmentReportByMetroParams = BaseParams & {
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
}

type ReadFutureWorkByClientParams = BaseParams & {
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
};

type ReadFutureWorkByParams = {
  groupBy?: string;
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
  clientId?: number;
  userIds?: number[];
};

type ReadMetroRecruitersParams = BaseParams & {
  metroId: number;
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
};

type ReadMetroClientsParams = BaseParams & {
  metroId: number;
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
};

type ReadClientStoresParams = BaseParams & {
  metroId: number;
  clientId: number;
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
};

type ReadDedicatedWorkerReportParams = {
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
};

type ReadTravelMerchandiserDedicatedReportParams = {
  roleId?: number;
  page: number;
  pageSize: number;
  sortBy: TableSortBy;
}

export function readTalentTableData(params: BaseParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_TALENT_TABLE_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/talent`, {
        params
      });
      dispatch({
        type: READ_TALENT_TABLE_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_TALENT_TABLE_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readUnfilledJobsData(params: BaseParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_JOBS_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/unfilledJobs`, {
        params
      });
      dispatch({
        type: READ_JOBS_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_JOBS_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readTasksData(params: BaseParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_TASKS_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/tasks`, {
        params
      });
      dispatch({
        type: READ_TASKS_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_TASKS_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readIssuesData(params: BaseParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_ISSUES_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/issues`, {
        params
      });
      dispatch({
        type: READ_ISSUES_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_ISSUES_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readPerformanceData(params: PerformanceDataParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_PERFORMANCE_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/performance`, {
        params
      });
      dispatch({
        type: READ_PERFORMANCE_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_PERFORMANCE_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readSlotsData(params: SlotsDataParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_SLOTS_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/slots`, {
        params
      });
      dispatch({
        type: READ_SLOTS_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_SLOTS_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readRecruitersDropdownData(): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_RECRUITER_DROPDOWN_DATA_REQUEST });
    try {
      const response = await apiRequest("get", `dashboard/recruitersDropdown`);
      dispatch({
        type: READ_RECRUITER_DROPDOWN_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_RECRUITER_DROPDOWN_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function setRecruiterIds(userIds: number[]): any {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({
        type: SET_RECRUITER_IDS_SUCCESS,
        contents: { recruiterIds: userIds }
      });
      return userIds;
    } catch {}
  };
};

export function readRecruitmentReportByMetroData(params : RecruitmentReportByMetroParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_RECRUITMENT_REPORT_BY_METRO_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/recruitmentReportByMetro`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_RECRUITMENT_REPORT_BY_METRO_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({
        type: READ_RECRUITMENT_REPORT_BY_METRO_DATA_FAILURE,
        error
      });
      throw response;
    }
  };
};

export function readFutureWorkByClientData(params: ReadFutureWorkByClientParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_FUTURE_WORK_BY_CLIENT_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/futureWorkByClient`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_FUTURE_WORK_BY_CLIENT_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_FUTURE_WORK_BY_CLIENT_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readFutureWorkByData(params: ReadFutureWorkByParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_FUTURE_WORK_BY_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/futureWorkBy`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_FUTURE_WORK_BY_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_FUTURE_WORK_BY_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readMetroRecruitersData(params: ReadMetroRecruitersParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_METRO_RECRUITERS_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/metroRecruiters`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_METRO_RECRUITERS_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_METRO_RECRUITERS_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readMetroClientsData(params: ReadMetroClientsParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_METRO_CLIENTS_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/metroClients`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_METRO_CLIENTS_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_METRO_CLIENTS_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readClientStoresData(params: ReadClientStoresParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_CLIENT_STORES_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/clientStores`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_CLIENT_STORES_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_CLIENT_STORES_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readDedicatedWorkerReportData(params: ReadDedicatedWorkerReportParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_DEDICATED_WORKER_REPORT_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/dedicatedWorkerReport`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_DEDICATED_WORKER_REPORT_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_DEDICATED_WORKER_REPORT_DATA_FAILURE, error });
      throw response;
    }
  };
};

export function readTravelMerchandiserDedicatedReport(params: ReadTravelMerchandiserDedicatedReportParams): any {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_REQUEST });
    try {
      const response = await apiRequest(
        "post",
        `dashboard/travelMerchandiserDedicatedReport`,
        {
          data: params
        }
      );
      dispatch({
        type: READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_SUCCESS,
        contents: response?.data || {}
      });
      return response?.data || {};
    } catch (response) {
      const error = response && response.error;
      dispatch({ type: READ_TRAVEL_MERCHANDISER_DEDICATED_REPORT_DATA_FAILURE, error });
      throw response;
    }
  };
};